import { createComputed, createMemo, createSignal, type Accessor } from "solid-js"
import type { SetStoreFunction } from "solid-js/store"
import { createJSONStorage, sessionStorageSource } from "@/storage/browser"
import { DEFAULT_PANEL_WIDTH, type RightSidebarTab } from "../layout-model"
import type { LayoutState } from "./state"

export type TabPanel = { opened: boolean; tab: RightSidebarTab }

const panelTabs: readonly RightSidebarTab[] = ["explorer", "changes", "context"]
const isPanelTab = (value: unknown): value is RightSidebarTab => panelTabs.some((tab) => tab === value)

/** A right sidebar panel as stored or linked, or undefined when malformed. */
export function decodeTabPanel(value: unknown): TabPanel | undefined {
  if (!value || typeof value !== "object") return undefined
  const { opened, tab } = value as { opened?: unknown; tab?: unknown }
  if (typeof opened !== "boolean" || !isPanelTab(tab)) return undefined
  return { opened, tab }
}

/**
 * Disposable cache: whether this tab's right sidebar is open and which panel it shows.
 * sessionStorage survives this tab's reloads only, so a reload keeps this tab's panel
 * even after another tab changes the shared layout preference, which new tabs start from.
 */
const tabPanel = createJSONStorage<TabPanel | undefined>(
  "mux.layout.right-sidebar.tab.v1",
  { decode: decodeTabPanel },
  sessionStorageSource,
)

/** Starts this tab with `panel` rather than the shared layout preference. Call before the layout loads. */
export function scopeTabRightSidebar(panel: TabPanel) {
  tabPanel.write(panel)
}

export function createLayoutRightSidebar(store: LayoutState, setStore: SetStoreFunction<LayoutState>, ready: Accessor<boolean>) {
  const [own, setOwn] = createSignal(tabPanel.read(undefined))
  const opened = createMemo(() => own()?.opened ?? store.rightSidebar?.opened ?? true)
  const tab = createMemo(() => own()?.tab ?? store.rightSidebar?.tab ?? "explorer")
  const update = (next: TabPanel) => {
    setOwn(next)
    tabPanel.write(next)
    // New tabs start from the most recent choice in any tab.
    setStore("rightSidebar", next)
  }
  // Fix the panel this tab loaded with once the layout preference is read.
  createComputed(() => {
    if (own() || !ready()) return
    const loaded = { opened: opened(), tab: tab() }
    setOwn(loaded)
    tabPanel.write(loaded)
  })
  const width = createMemo(() => {
    const saved = store.rightSidebar?.widths?.[tab()]
    return typeof saved === "number" && Number.isFinite(saved) && saved > 0
      ? saved
      : DEFAULT_PANEL_WIDTH
  })

  return {
    opened,
    width,
    tab,
    setTab(next: RightSidebarTab) {
      update({ opened: opened(), tab: next })
    },
    open() {
      update({ opened: true, tab: tab() })
    },
    close() {
      update({ opened: false, tab: tab() })
    },
    toggle() {
      update({ opened: !opened(), tab: tab() })
    },
    resize(width: number) {
      setStore("rightSidebar", "widths", tab(), width)
    },
  }
}
